// Hero.js — Slider principal da home com livro em destaque.
// Conforme novo feedback do mockup:
//  - Sem bolinhas (dots) embaixo
//  - Setas transparentes por padrão; ganham fundo só no hover/active
//  - Gradiente inferior fundindo o slide com o background do site
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "react-router-dom";
import useHomeConfig from "../../hooks/useHomeConfig";
import BlurText from "../ui/BlurText";

// Seta customizada — sem fundo por padrão, ganha bg no hover/active
const HeroArrow = ({ onClick, dir }) => (
  <button
    onClick={onClick}
    aria-label={dir === "prev" ? "Anterior" : "Próximo"}
    className={`absolute top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full
                flex items-center justify-center text-white text-3xl
                bg-transparent border border-line/0
                hover:bg-[#5b5bd6] hover:border-line
                active:bg-[#4444b8]
                transition-all duration-200
                ${dir === "prev" ? "left-3 md:left-6" : "right-3 md:right-6"}`}
  >
    <i className={`uil uil-angle-${dir === "prev" ? "left" : "right"}-b`} />
  </button>
);

const Hero = () => {
  const [livros, setLivros] = useState([]);
  const API_URL = process.env.REACT_APP_API;
  const main = process.env.REACT_APP_MAIN;
  const config = useHomeConfig();
  const heroImagens = config?.hero_imagens || [];
  const usarImagens = heroImagens.length > 0;

  useEffect(() => {
    const fetchLivros = async () => {
      try {
        const r = await fetch(API_URL + "ebooks-recentes");
        if (r.ok) setLivros((await r.json()).slice(0, 4));
      } catch (e) {
        console.error("Erro ao carregar livros do hero:", e);
      }
    };
    fetchLivros();
  }, [API_URL]);

  const settings = {
    dots: false, // <-- removidas conforme mockup
    arrows: true,
    infinite: (usarImagens ? heroImagens.length : livros.length) > 1,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 6000,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <HeroArrow dir="prev" />,
    nextArrow: <HeroArrow dir="next" />,
  };

  // O recuo do topo passou a sair de --nav-h, a mesma variável que TODAS as
  // outras páginas usam. Aqui ele estava chumbado em 180/230px enquanto o
  // cabeçalho é fixo e muda de altura por breakpoint: abaixo de 640px o banner
  // nascia ~24px debaixo do header, e entre 640 e 767px (onde a faixa laranja
  // aparece e --nav-h vale 250px) o corte passava de 70px. Chumbar de novo aqui
  // significaria refazer essa conta toda vez que uma faixa do cabeçalho mudar.
  // Os DOIS returns usam a variável — o de carregamento também, senão a home
  // daria um pulo de dezenas de pixels no instante em que as imagens chegam.
  if (!usarImagens && livros.length === 0) {
    return (
      <section className="pt-[var(--nav-h)] pb-10 px-5 bg-insigne-bg">
        {/* A altura acompanha a do conteudo real em cada faixa. Com `h-[400px]`
            sem variante, o celular reservava 400px para um bloco que ocupa bem
            menos, e a pagina dava um pulo para cima no instante em que as capas
            chegavam — justamente no aparelho onde o carregamento demora mais e
            o salto e mais visivel. */}
        <div className="max-w-[1280px] mx-auto h-[240px] sm:h-[320px] md:h-[400px] rounded-painel surface-hero animate-pulse" />
      </section>
    );
  }

  return (
    <section className="pt-[var(--nav-h)] pb-2 bg-insigne-bg">
      <div className="max-w-[1280px] mx-auto px-3 md:px-6 relative">
        <Slider {...settings}>
          {usarImagens
            ? heroImagens.map((path, i) => (
                <div key={i}>
                  <Link to={main + "livros"} className="block relative overflow-hidden rounded-painel">
                    <img
                      src={`${API_URL}public/${path}`}
                      alt="Destaque"
                      /* A arte cadastrada no painel é 2173x724 (~3:1) e este
                         <img> tinha altura fixa com object-cover: num celular de
                         390px a caixa ficava ~366x360 (~1:1) e o cover jogava
                         fora cerca de dois terços da largura — justamente onde o
                         título e o nome da autora estão desenhados DENTRO da
                         arte. Por isso o cliente via "a imagem cortada": o que
                         sumia era o texto, não a moldura.
                         No celular quem manda agora é a proporção, não a altura.
                         O custo, aceito de propósito: nesse mesmo celular de
                         390px o banner vira uma faixa de ~122px de altura — a
                         arte aparece inteira, porém pequena. Ficou assim para
                         testar o CSS antes de gastar migration, endpoint e campo
                         de painel com uma arte dedicada de celular.
                         Do md para cima nada muda: volta a altura fixa de 460px,
                         onde o corte era de ~11% e ninguém reclamou.
                         O object-cover fica: se um dia subirem arte de outra
                         proporção, cortar de leve é melhor do que as tarjas do
                         object-contain, que ainda brigariam com o gradiente
                         logo abaixo — ele é desenhado supondo imagem cheia. */
                      className="w-full aspect-[2173/724] md:aspect-auto md:h-[460px] object-cover"
                      /* Medidas da arte em produção. Servem só para o navegador
                         reservar o espaço antes de o CSS chegar; quem decide o
                         desenho final continua sendo a classe acima, que vence
                         os atributos. Sem elas, a home dá um salto de layout no
                         meio do carregamento. */
                      width={2173}
                      height={724}
                    />
                    {/* A altura do gradiente virou proporcional no celular por
                        causa da mudança acima: com o banner em proporção, num
                        aparelho de 390px ele tem ~122px de altura, e os 128px
                        fixos deste degradê cobririam a arte INTEIRA — o título
                        que acabamos de parar de cortar apagaria em fade no
                        lugar. Um quarto da caixa acompanha qualquer tamanho.
                        Do md para cima a altura fixa continua, que é onde ela
                        foi desenhada. */}
                    <div className="absolute bottom-0 left-0 right-0 h-1/4 md:h-32 pointer-events-none bg-gradient-to-b from-transparent to-insigne-bg" />
                  </Link>
                </div>
              ))
            : livros.map((livro) => (
            <div key={livro.id}>
              {/* Wrapper do slide com overflow para o gradiente inferior */}
              <div className="relative overflow-hidden rounded-painel">
                {/* Slide */}
                <div
                  className="relative surface-hero
                             min-h-[360px] md:min-h-[460px] grid grid-cols-1 md:grid-cols-2 items-center
                             gap-6 p-6 md:p-12"
                >
                  {/* Ilustração / capa */}
                  <div className="flex justify-center md:justify-end">
                    <img
                      src={`${API_URL}public/${livro.image}`}
                      alt={livro.titulo}
                      className="max-h-[300px] md:max-h-[400px] drop-shadow-2xl"
                    />
                  </div>

                  {/* Texto do slide */}
                  <div className="text-white text-center md:text-left">
                    <p className="text-sm md:text-base opacity-90 mb-3">
                      {(() => {
                        const a = Array.isArray(livro.autores) ? livro.autores.find((x) => x != null) : null;
                        return a ? (typeof a === "object" ? a.nome : a) : "Editora Insigne Acadêmica";
                      })()}
                    </p>
                    {/* Título entra palavra por palavra — é a primeira coisa
                        que a pessoa lê ao abrir o site. Meio segundo, uma vez. */}
                    <BlurText
                      texto={livro.titulo}
                      as="h1"
                      className="font-serif italic text-3xl md:text-5xl lg:text-6xl leading-tight mb-4"
                    />
                    {livro.subtitulo && (
                      <p className="font-serif italic text-base md:text-xl opacity-90 mb-6">
                        {livro.subtitulo}
                      </p>
                    )}
                    <Link
                      to={main + "livros/" + livro.id}
                      className="inline-block px-6 py-2.5 rounded-controle font-semibold uppercase text-sm
                                 bg-insigne-orange text-paper border-2 border-line shadow-insigne-btn
                                 transition-all duration-300 hover:scale-105"
                    >
                      Ver livro
                    </Link>
                  </div>
                </div>

                {/* Gradiente inferior: fundo do slide → cor do site */}
                <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none
                                bg-gradient-to-b from-transparent to-insigne-bg" />
              </div>
            </div>
          ))}
        </Slider>

        {/* AQUI HAVIA UM "VER TODOS OS LIVROS", removido em 02/09/2026.
            Ele existia porque no celular não havia caminho visível até o
            acervo. O caminho passou a ser o botão LIVROS da barra do
            cabeçalho (ver `Header.js`), que agora aparece também no celular —
            um botão gigante embaixo do banner resolvia o mesmo problema
            empurrando os livros em destaque para fora da primeira tela.
            O próprio banner continua sendo um <Link> para o catálogo. */}
      </div>
    </section>
  );
};

export default Hero;
